import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SearchInput from '../components/SearchInput';

// Import the same projects data from OurWork
import { galleryItems } from './OurWork';

// Define the type for gallery items
type GalleryItem = {
  id: number;
  type: 'image' | 'video';
  src: string;
  category: string;
  title: string;
  description: string;
  videoId?: string;
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [filteredResults, setFilteredResults] = useState<GalleryItem[]>([]);

  useEffect(() => {
    if (query) {
      const lowerQuery = query.toLowerCase();
      const filtered = galleryItems.filter(project => 
        project.title.toLowerCase().includes(lowerQuery) ||
        project.description.toLowerCase().includes(lowerQuery) ||
        project.category.toLowerCase().includes(lowerQuery)
      );
      setFilteredResults(filtered);
    }
  }, [query]);

  return (
    <div className="min-h-screen bg-dark-gray pt-24">
      <div className="container mx-auto px-4 py-12">
        {/* Search Bar */}
        <div className="max-w-3xl mx-auto mb-12 px-4">
          <SearchInput
            initialQuery={query}
            placeholder="Search our work..."
            className="max-w-3xl mx-auto"
            autoFocus={true}
            showSuggestions={true}
            galleryData={galleryItems}
          />
        </div>

        {/* Search Results */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-8">
            {query ? `Search Results for "${query}"` : 'Search Our Work'}
          </h2>
          
          {filteredResults.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResults.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-800 hover:border-burnt-orange/50 transition-colors group"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'https://placehold.co/600x400/1f2937/6b7280?text=Image+Not+Found';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4">
                      <div className="mb-2">
                        <h3 className="text-white font-semibold text-lg">{item.title}</h3>
                        <p className="text-burnt-orange text-sm font-medium capitalize">{item.category}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="inline-block bg-gray-800 text-burnt-orange text-xs px-3 py-1 rounded-full">
                        {item.category}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : query ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No results found for "{query}"</p>
              <p className="text-gray-500 mt-2">Try different keywords or check out our work below</p>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Enter a search term to find projects</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;
